import { AxiosError } from 'axios';
import { useQuery, UseQueryOptions } from 'react-query';
import client from 'client';
import useGroupId, { isPreviewMode } from './useGroupId';
import convertEventResponse from './convertEventResponse';

export type AttendanceType = 'HYBRID' | 'VIRTUAL' | 'IN_PERSON';

export type Event = {
    accountId: number;
    id: number;
    groupId: number;
    uniqueName: string;
    name: string;
    created: number;
    startDate: number;
    endDate: number;
    timeZone: string;
    localStartDate: string;
    localEndDate: string;
    headerLogoUrl: string;
    homepageUrl: string;
    faviconUrl?: string;
    supportEmail?: string;
    type: string;
    attendanceType?: AttendanceType;
};

export type EventResponse = {
    'account-id': number;
    id: number;
    groupId: number;
    'unique-name': number | string;
    name: string;
    created: number;
    'start-date': number;
    'end-date': number;
    'time-zone-id': string;
    'homepage-url': string;
    'header-logo-url': string;
    'favicon-url'?: string;
    'logo-set': {
        blur: string;
        facebook: string;
        large: string;
        medium: string;
        small: string;
    };
    'support-email'?: string;
    type: string;
    attendanceType: AttendanceType;
};

const fetchEventByUniqueName = async (uniqueName: string, preview = false) => {
    const {
        data: { event },
    } = await client().get<{ event: EventResponse }>(`/api/v1/event/unique-name/${uniqueName}`, {
        params: {
            preview,
        },
    });
    return event;
};

export const fetchEventsByDomain = async (domain: string) => {
    const params = new URLSearchParams();
    params.append('domain', domain);
    const response = await client().get(`/api/v1/event/byDomain`, { params });
    if (response.status !== 200) {
        throw response;
    }
    return response.data;
};

const useEvent = (eventUniqueName: string, options: UseQueryOptions<EventResponse> = {}) => {
    const { data: groupId } = useGroupId(eventUniqueName);
    const isPreview = isPreviewMode(groupId);

    const event = useQuery<EventResponse, AxiosError>(
        ['event', eventUniqueName, isPreview],
        () => fetchEventByUniqueName(eventUniqueName, isPreview),
        {
            ...options,
            enabled: !!groupId,
            // refetching time grows exponentially, hence when we have an error and default refetching 3,
            // there is a long time gap to receive an error on ui
            retry: 1,
        }
    );

    const { data } = event;

    return {
        ...event,
        data: (data ? convertEventResponse(data) : null) as Event,
    };
};

export default useEvent;
